import { useEffect, useState } from "react";
import { ShieldCheck, Check, X, Scale, Globe2, MessageSquare, SlidersHorizontal, Ban } from "lucide-react";
import api from "@/lib/api";
import { timeAgo } from "@/lib/constants";

const CATEGORY_COLORS = {
  ai: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  human: "text-sky-400 border-sky-500/40 bg-sky-500/10",
  signal: "text-amber-400 border-amber-500/40 bg-amber-500/10",
  execution: "text-violet-400 border-violet-500/40 bg-violet-500/10",
  system: "text-zinc-400 border-zinc-500/40 bg-zinc-500/10",
};

const Card = ({ title, icon: Icon, children, testId }) => (
  <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5" data-testid={testId}>
    <div className="flex items-center gap-2 mb-4 pb-3 border-b border-zinc-800">
      <Icon className="w-4 h-4 text-emerald-400" strokeWidth={1.5} />
      <h3 className="text-sm font-semibold text-zinc-200">{title}</h3>
    </div>
    {children}
  </div>
);

export default function Governance() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/governance").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return <div className="font-mono text-zinc-500 text-sm animate-pulse" data-testid="governance-loading">LOADING GOVERNANCE CENTRE…</div>;
  }

  const p = data.policy;

  return (
    <div className="space-y-6" data-testid="governance-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-100 flex items-center gap-3">
          <ShieldCheck className="w-7 h-7 text-emerald-400" strokeWidth={1.5} /> Governance Centre
        </h1>
        <p className="text-sm text-zinc-400 mt-1">{p.org}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="Broker Licences & Jurisdictions" icon={Scale} testId="gov-licences">
          <div className="space-y-3">
            {p.licences.map((l, i) => (
              <div key={i} className="border border-zinc-800 rounded-sm p-3 bg-zinc-950/50">
                <div className="text-xs font-semibold text-zinc-200">{l.body}</div>
                <div className="text-[11px] font-mono text-emerald-500 mt-0.5">{l.number}</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">{l.scope}</div>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Permitted Communications" icon={Check} testId="gov-permitted">
          <div className="space-y-2">
            {p.permitted.map((item, i) => (
              <div key={i} className="flex items-start gap-2 text-xs text-zinc-300">
                <Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" /> {item}
              </div>
            ))}
          </div>
        </Card>

        <Card title="Prohibited Communications" icon={Ban} testId="gov-prohibited">
          <div className="space-y-2">
            {p.prohibited.map((item, i) => (
              <div key={i} className="flex items-start gap-2 text-xs text-zinc-300">
                <X className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" /> {item}
              </div>
            ))}
          </div>
        </Card>

        <Card title="Approved Languages" icon={Globe2} testId="gov-languages">
          <div className="flex flex-wrap gap-2">
            {p.languages.map((l) => (
              <span key={l} className="px-2.5 py-1 rounded-full text-xs font-medium border border-zinc-700 bg-zinc-800/60 text-zinc-300">{l}</span>
            ))}
          </div>
        </Card>

        <Card title="Approved Channels" icon={MessageSquare} testId="gov-channels">
          <div className="flex flex-wrap gap-2">
            {p.channels.map((c) => (
              <span key={c} className="px-2.5 py-1 rounded-full text-xs font-medium border border-emerald-500/40 bg-emerald-500/10 text-emerald-400">{c}</span>
            ))}
          </div>
        </Card>

        <Card title="Human Approval Thresholds" icon={SlidersHorizontal} testId="gov-thresholds">
          <div className="space-y-3">
            {p.thresholds.map((t, i) => (
              <div key={i} className="text-xs">
                <div className="text-zinc-200 font-semibold">{t.rule}</div>
                <div className="text-zinc-500 mt-0.5">→ {t.action}</div>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Restricted Claims (auto-scanned)" icon={Ban} testId="gov-restricted-claims">
          <div className="flex flex-wrap gap-2">
            {p.restricted_claims.map((c) => (
              <span key={c} className="px-2.5 py-1 rounded-full text-xs font-mono border border-red-500/40 bg-red-500/10 text-red-400">“{c}”</span>
            ))}
          </div>
          <p className="text-[11px] text-zinc-400 mt-3">Every AI-generated message is scanned against this list before execution. Violations block the action automatically.</p>
        </Card>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md" data-testid="audit-log">
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="text-base font-semibold text-zinc-200">Complete Audit Log</h3>
          <span className="text-[10px] font-mono text-zinc-500 tracking-widest uppercase">{data.audit.length} entries · immutable</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="border-b border-zinc-800 text-left">
                <th className="px-6 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Time</th>
                <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Actor</th>
                <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Action</th>
                <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Client</th>
                <th className="px-6 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Detail</th>
              </tr>
            </thead>
            <tbody>
              {data.audit.map((a) => (
                <tr key={a.id} className="border-b border-zinc-800/60 hover:bg-zinc-800/50 transition-colors duration-150" data-testid={`audit-row-${a.action}`}>
                  <td className="px-6 py-3 text-[11px] font-mono text-zinc-500 whitespace-nowrap">{timeAgo(a.ts)}</td>
                  <td className="px-4 py-3 text-xs text-zinc-300 whitespace-nowrap">{a.actor}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-semibold border ${CATEGORY_COLORS[a.category] || CATEGORY_COLORS.system}`}>
                      {a.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-400 whitespace-nowrap">{a.client_name || "—"}</td>
                  <td className="px-6 py-3 text-xs text-zinc-400 max-w-md truncate" title={a.detail}>{a.detail}</td>
                </tr>
              ))}
              {data.audit.length === 0 && (
                <tr><td colSpan={5} className="px-6 py-8 text-center text-sm text-zinc-500">No audit entries yet — run any workflow action.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
